import axios from 'axios';
import { baseURL } from '../config';

// Configure axios defaults for CORS
axios.defaults.withCredentials = false;

// Add request interceptor for CORS debugging
axios.interceptors.request.use(
    (config) => {
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Admin API service for user management
export const adminApi = {
    // Create a new user
    createUser: async (token, userData) => {
        try {
            console.log('API: Creating user with data:', userData);
            console.log('API: Request URL:', `${baseURL}/api/admin/users`);
            
            // Handle permissions - if they're already integers, use them; if they're strings, convert them
            let permissionIds = [];
            
            if (userData.Permissions && Array.isArray(userData.Permissions)) {
                if (typeof userData.Permissions[0] === 'number') {
                    // Permissions are already integers
                    permissionIds = userData.Permissions;
                    console.log('API: Permissions are already integers:', permissionIds);
                } else {
                    // Permissions are strings, need to convert them
                    const permissionKeyToInt = {
                        'Settings': 1,
                        'Settings.LPManagement': 2,
                        'Settings.Security': 3,
                        'Settings.DataManagement': 4,
                        'Settings.NotificationAppearance': 5,
                        'Settings.Strategies': 6,
                        'Settings.LPManagement.Symbols': 7,
                        'Settings.LPManagement.EditLP': 8,
                        'Settings.LPManagement.LPConfiguration': 9,
                        'Settings.LPManagement.ConnectionSetting': 10,
                        'Settings.LPManagement.DeleteLP': 11,
                        'Settings.LPManagement.Symbols.View': 12,
                        'Settings.LPManagement.Symbols.Edit': 13,
                        'Settings.LPManagement.Symbols.Create': 14,
                        'Settings.LPManagement.Symbols.Delete': 15,
                        'Settings.LPManagement.LPConfiguration.View': 16,
                        'Settings.LPManagement.LPConfiguration.Edit': 17,
                        'Settings.LPManagement.LPConfiguration.Create': 18,
                        'Settings.LPManagement.LPConfiguration.Delete': 19,
                        'Settings.LPManagement.EditLP.View': 20,
                        'Settings.LPManagement.EditLP.Edit': 21,
                        'Settings.LPManagement.EditLP.Delete': 22,
                        'Settings.LPManagement.ConnectionSetting.View': 23,
                        'Settings.LPManagement.ConnectionSetting.Edit': 24,
                        'Settings.DataManagement.View': 25,
                        'Settings.DataManagement.Edit': 26,
                        'Settings.DataManagement.Create': 27,
                        'Settings.DataManagement.Delete': 28,
                        'Settings.Strategies.View': 29,
                        'Settings.Strategies.Edit': 30,
                        'Settings.Strategies.Create': 31,
                        'Settings.Strategies.Delete': 32,
                        'Dashboard.MarketWatchSection': 33,
                        'Dashboard.MonitoringSection': 34,
                        'Dashboard.TradePanel': 35,
                        'Dashboard.TradePanel.FixLogs': 36,
                        'Dashboard.TradePanel.TradeLogs': 37,
                        'Dashboard.QuickOrder': 38,
                        'Dashboard.SpreadOrder': 39
                    };
                    
                    permissionIds = userData.Permissions
                        .map(key => permissionKeyToInt[key] || 0)
                        .filter(id => id > 0);
                    console.log('API: Converted permission keys to IDs:', permissionIds);
                }
            }
            
            console.log('API: Original permissions:', userData.Permissions);
            console.log('API: Final permission IDs:', permissionIds);
            
            // Send data directly without request wrapper
            const requestData = {
                Email: userData.Email,
                FirstName: userData.FirstName,
                LastName: userData.LastName,
                Password: userData.Password,
                Role: userData.Role,
                Status: userData.Status,
                PermissionIds: permissionIds  // Changed from Permissions to PermissionIds
            };
            
            console.log('API: Request data:', JSON.stringify(requestData, null, 2));
            console.log('API: Field validation:');
            console.log('- Email:', requestData.Email, '(type:', typeof requestData.Email, ')');
            console.log('- FirstName:', requestData.FirstName, '(type:', typeof requestData.FirstName, ')');
            console.log('- LastName:', requestData.LastName, '(type:', typeof requestData.LastName, ')');
            console.log('- Password:', requestData.Password, '(type:', typeof requestData.Password, ')');
            console.log('- Role:', requestData.Role, '(type:', typeof requestData.Role, ')');
            console.log('- Status:', requestData.Status, '(type:', typeof requestData.Status, ')');
            console.log('- PermissionIds:', requestData.PermissionIds, '(type:', typeof requestData.PermissionIds, ')');
            
            const response = await axios.post(`${baseURL}/api/admin/users`, requestData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            });
            
            console.log('API: Response:', response.data);
            return {
                success: true,
                data: response.data,
                message: 'User created successfully'
            };
        } catch (error) {
            console.error('Error creating user:', error);
            console.error('Error response:', error.response?.data);
            return {
                success: false,
                error: error.response?.data?.message || 'Failed to create user'
            };
        }
    },

    // E1: Get all users - Real API implementation
    getUsers: async (token) => {
        try {
            console.log('API: Fetching all users from:', `${baseURL}/api/admin/users`);

            const response = await axios.get(`${baseURL}/api/admin/users`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            });

            console.log('API: getUsers response:', response.data);

            // Handle different response formats
            if (Array.isArray(response.data)) {
                return {
                    success: true,
                    data: response.data
                };
            } else if (response.data?.data) {
                return {
                    success: true,
                    data: response.data.data
                };
            } else {
                return {
                    success: true,
                    data: response.data
                };
            }
        } catch (error) {
            console.error('Error fetching users:', error);
            console.error('Error response:', error.response?.data);
            return {
                success: false,
                error: error.response?.data?.message || 'Failed to fetch users'
            };
        }
    },

    // E2: Update user - Real API implementation
    updateUser: async (token, userId, userData) => {
        try {
            console.log('API: Updating user:', userId, userData);
            console.log('API: Request URL:', `${baseURL}/api/admin/users/${userId}`);

            const response = await axios.put(
                `${baseURL}/api/admin/users/${userId}`,
                userData,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    }
                }
            );

            console.log('API: updateUser response:', response.data);

            // Handle 204 No Content (successful update with no body)
            if (response.status === 204) {
                return {
                    success: true,
                    data: { ...userData, id: userId },
                    message: 'User updated successfully'
                };
            }

            return {
                success: true,
                data: response.data?.data || response.data,
                message: 'User updated successfully'
            };
        } catch (error) {
            console.error('Error updating user:', error);
            console.error('Error response:', error.response?.data);
            return {
                success: false,
                error: error.response?.data?.message || 'Failed to update user'
            };
        }
    },

    // E3: Delete user - Real API implementation
    deleteUser: async (token, userId) => {
        try {
            console.log('API: Deleting user:', userId);
            console.log('API: Request URL:', `${baseURL}/api/admin/users/${userId}`);

            const response = await axios.delete(
                `${baseURL}/api/admin/users/${userId}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    }
                }
            );

            console.log('API: deleteUser response:', response.status, response.data);

            // Handle 204 No Content (successful delete with no body)
            if (response.status === 204 || response.status === 200) {
                return {
                    success: true,
                    message: 'User deleted successfully'
                };
            }

            return {
                success: true,
                data: response.data,
                message: 'User deleted successfully'
            };
        } catch (error) {
            console.error('Error deleting user:', error);
            console.error('Error response:', error.response?.data);
            return {
                success: false,
                error: error.response?.data?.message || 'Failed to delete user'
            };
        }
    },

};

// ============================================
// PERMISSION MANAGEMENT APIs
// ============================================

/**
 * Get all available permissions grouped by category
 * @returns {Promise} - Categories with permissions
 */
export const getAllPermissions = async () => {
  try {
    console.log('=== API: getAllPermissions ===');
    console.log('API: Request URL:', `${baseURL}/api/admin/permissions`);
    
    // Check if token exists
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }
    console.log('API: Token exists:', token ? 'Yes' : 'No');
    
    const response = await axios.get(`${baseURL}/api/admin/permissions`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      }
    });
    
    console.log('API: Response status:', response.status);
    console.log('API: Response data:', response.data);
    
    // Handle direct array response (current API format)
    if (Array.isArray(response.data)) {
      console.log('API: Received direct array response');
      return { data: response.data };
    }
    // Handle wrapped response format (if API changes)
    else if (response.data && response.data.data) {
      console.log('API: Received wrapped response format');
      return response.data;
    }
    // Fallback
    else {
      console.log('API: Unknown response format, returning as-is');
    return response.data;
    }
  } catch (error) {
    console.error('=== API ERROR: getAllPermissions ===');
    console.error('Error fetching permissions:', error);
    console.error('Error status:', error.response?.status);
    console.error('Error response data:', error.response?.data);
    console.error('Error message:', error.message);
    throw error;
  }
};

/**
 * Get user's current permissions
 * @param {string} userId - User ID
 * @returns {Promise} - User permissions with granted flags
 */
export const getUserPermissions = async (userId) => {
  try {
    console.log('=== API: getUserPermissions ===');
    console.log('API: User ID:', userId);
    console.log('API: Request URL:', `${baseURL}/api/admin/users/${userId}/permissions`);
    
    // Check if token exists
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }
    console.log('API: Token exists:', token ? 'Yes' : 'No');
    
    const response = await axios.get(`${baseURL}/api/admin/users/${userId}/permissions`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      }
    });
    
    console.log('API: Response status:', response.status);
    console.log('API: Response data:', response.data);
    console.log('API: Response data type:', typeof response.data);
    console.log('API: Response data length:', response.data?.data?.length || response.data?.length || 'No data array');
    console.log('API: Response success:', response.data?.success);
    console.log('API: Response message:', response.data?.message);
    
    // Handle different response formats
    if (Array.isArray(response.data)) {
      // Direct array response
      console.log('API: Direct array response for user permissions');
      console.log('API: Number of permissions:', response.data.length);
      console.log('API: First permission object:', response.data[0]);
      console.log('API: Has isGranted field:', response.data[0]?.hasOwnProperty('isGranted'));
      console.log('API: Sample permission keys:', response.data.slice(0, 3).map(p => p.permissionKey));
      return response.data;
    } else if (response.data?.data) {
      // Wrapped response
      console.log('API: Wrapped response for user permissions');
      console.log('API: Number of permissions:', response.data.data.length);
      console.log('API: First permission object:', response.data.data[0]);
      console.log('API: Has isGranted field:', response.data.data[0]?.hasOwnProperty('isGranted'));
      console.log('API: Sample permission keys:', response.data.data.slice(0, 3).map(p => p.permissionKey));
      return response.data;
    } else {
      console.log('API: Unknown response format:', response.data);
    return response.data;
    }
  } catch (error) {
    console.error('=== API ERROR: getUserPermissions ===');
    console.error('Error fetching user permissions:', error);
    console.error('Error status:', error.response?.status);
    console.error('Error response data:', error.response?.data);
    console.error('Error message:', error.message);
    throw error;
  }
};

/**
 * Update user's permissions
 * @param {string} userId - User ID
 * @param {Array<number>} permissionIds - Array of permission IDs to assign
 * @returns {Promise} - Update result
 */
export const updateUserPermissions = async (userId, permissionIds) => {
  try {
    console.log('=== API: updateUserPermissions ===');
    console.log('API: User ID:', userId);
    console.log('API: Permission IDs:', permissionIds);
    console.log('API: Permission IDs count:', permissionIds?.length);
    console.log('API: Request URL:', `${baseURL}/api/admin/users/${userId}/permissions`);
    
    // Check if token exists
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }
    console.log('API: Token exists:', token ? 'Yes' : 'No');
    
    // Validate that we have permission IDs to send
    if (!permissionIds || permissionIds.length === 0) {
      console.error('API: No permission IDs found!');
      throw new Error('No permission IDs found to send');
    }
    
    console.log('API: Using permission IDs directly:', permissionIds);
    
    // Try different request formats that the backend might expect
    const requestData = {
      permissionIds: permissionIds  // Send permission IDs directly (without request wrapper)
    };
    
    console.log('API: Request data:', JSON.stringify(requestData, null, 2));
    console.log('API: Permission IDs count:', permissionIds.length);
    console.log('API: Permission IDs type:', typeof permissionIds);
    console.log('API: Permission IDs is array:', Array.isArray(permissionIds));
    console.log('API: Permission IDs values:', permissionIds);
    console.log('API: Using field name: permissionIds (direct)');
    console.log('API: Request structure:', {
      permissionIds: permissionIds
    });
    
    let response;
    try {
      // Try direct format first
      response = await axios.put(
        `${baseURL}/api/admin/users/${userId}/permissions`,
        requestData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        }
      );
    } catch (error) {
      console.log('API: Direct format failed, trying wrapped format...');
      
      // Try wrapped format as fallback
      const wrappedRequestData = {
        request: {
          permissionIds: permissionIds
        }
      };
      
      console.log('API: Trying wrapped request data:', JSON.stringify(wrappedRequestData, null, 2));
      
      response = await axios.put(
      `${baseURL}/api/admin/users/${userId}/permissions`,
        wrappedRequestData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        }
      );
    }
    
    console.log('API: Response status:', response.status);
    console.log('API: Response data:', response.data);
    
    // Return success response
    return {
      success: true,
      data: response.data,
      message: 'Permissions updated successfully'
    };
  } catch (error) {
    console.error('=== API ERROR: updateUserPermissions ===');
    console.error('Error updating user permissions:', error);
    console.error('Error status:', error.response?.status);
    console.error('Error response data:', error.response?.data);
    console.error('Error message:', error.message);
    
    // Return error response
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to update permissions',
      data: error.response?.data
    };
  }
};

/**
 * Get all users with pagination (already exists, but ensure it's working)
 * @returns {Promise} - List of users
 */
export const getAllUsers = async () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }
    
    const response = await axios.get(`${baseURL}/api/admin/users`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching all users:', error);
    throw error;
  }
};

// Standalone createUser function - delegates to adminApi.createUser
export const createUser = async (userData) => {
    const token = localStorage.getItem('token');
    if (!token) {
        return {
            success: false,
            error: 'No authentication token found'
        };
    }
    return adminApi.createUser(token, userData);
};

// ============================================
// ADMIN STRATEGY MANAGEMENT APIs
// ============================================

/**
 * Get all strategies across all users (admin view)
 * @param {Object} filters - Optional filters { userId, status, accountNumber }
 * @returns {Promise} - List of strategies with user details
 */
export const getAllStrategies = async (filters = {}) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    // Build query string from filters
    const queryParams = new URLSearchParams();
    if (filters.userId) queryParams.append('userId', filters.userId);
    if (filters.status) queryParams.append('status', filters.status);
    if (filters.accountNumber) queryParams.append('accountNumber', filters.accountNumber);

    const queryString = queryParams.toString();
    const url = `${baseURL}/api/admin/strategies${queryString ? `?${queryString}` : ''}`;

    console.log('API: Fetching all strategies from:', url);

    const response = await axios.get(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      }
    });

    console.log('API: getAllStrategies response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching all strategies:', error);
    throw error;
  }
};

/**
 * Get strategy statistics for dashboard cards
 * @returns {Promise} - Strategy statistics (counts, totals, etc.)
 */
export const getStrategyStats = async () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    console.log('API: Fetching strategy statistics');

    const response = await axios.get(`${baseURL}/api/admin/strategies/stats`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      }
    });

    console.log('API: getStrategyStats response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching strategy stats:', error);
    throw error;
  }
};

/**
 * Admin override start a strategy (bypasses ownership validation)
 * @param {string} strategyId - Strategy ID to start
 * @returns {Promise} - Override start result
 */
export const adminOverrideStart = async (strategyId) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    console.log('API: Admin override starting strategy:', strategyId);

    const response = await axios.post(
      `${baseURL}/api/admin/strategies/${strategyId}/override-start`,
      {},
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        }
      }
    );

    console.log('API: adminOverrideStart response:', response.data);
    return {
      success: true,
      data: response.data,
      message: 'Strategy started successfully (Admin Override)'
    };
  } catch (error) {
    console.error('Error admin override starting strategy:', error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to start strategy',
      data: error.response?.data
    };
  }
};

/**
 * Admin override stop a strategy (bypasses ownership validation)
 * @param {string} strategyId - Strategy ID to stop
 * @returns {Promise} - Override stop result
 */
export const adminOverrideStop = async (strategyId) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    console.log('API: Admin override stopping strategy:', strategyId);

    const response = await axios.post(
      `${baseURL}/api/admin/strategies/${strategyId}/override-stop`,
      {},
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        }
      }
    );

    console.log('API: adminOverrideStop response:', response.data);
    return {
      success: true,
      data: response.data,
      message: 'Strategy stopped successfully (Admin Override)'
    };
  } catch (error) {
    console.error('Error admin override stopping strategy:', error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to stop strategy',
      data: error.response?.data
    };
  }
};

/**
 * Admin force stop a strategy (bypasses ownership validation)
 * @param {string} strategyId - Strategy ID to force stop
 * @returns {Promise} - Force stop result
 */
export const forceStopStrategy = async (strategyId) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    console.log('API: Force stopping strategy:', strategyId);

    const response = await axios.post(
      `${baseURL}/api/admin/strategies/${strategyId}/force-stop`,
      {},
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        }
      }
    );

    console.log('API: forceStopStrategy response:', response.data);
    return {
      success: true,
      data: response.data,
      message: 'Strategy force stopped successfully'
    };
  } catch (error) {
    console.error('Error force stopping strategy:', error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to force stop strategy',
      data: error.response?.data
    };
  }
};

/**
 * Bulk control strategies (start/stop multiple strategies)
 * @param {Array<string>} strategyIds - Array of strategy IDs
 * @param {string} action - Action to perform: 'start' or 'stop'
 * @returns {Promise} - Bulk action result
 */
export const bulkControlStrategies = async (strategyIds, action) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    console.log('API: Bulk control strategies:', { strategyIds, action });

    const response = await axios.post(
      `${baseURL}/api/admin/strategies/bulk-control`,
      {
        strategyIds,
        action
      },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        }
      }
    );

    console.log('API: bulkControlStrategies response:', response.data);
    return {
      success: true,
      data: response.data,
      message: `Bulk ${action} completed successfully`
    };
  } catch (error) {
    console.error('Error in bulk control:', error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to perform bulk action',
      data: error.response?.data
    };
  }
};

/**
 * Toggle strategy enabled/disabled status
 * @param {string} strategyId - Strategy ID
 * @param {boolean} isEnabled - New enabled status
 * @returns {Promise} - Toggle result
 */
export const toggleStrategyStatus = async (strategyId, isEnabled) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    console.log('API: Toggling strategy status:', { strategyId, isEnabled });

    const response = await axios.patch(
      `${baseURL}/api/admin/strategies/${strategyId}/status`,
      { isEnabled },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        }
      }
    );

    console.log('API: toggleStrategyStatus response:', response.data);
    return {
      success: true,
      data: response.data,
      message: `Strategy ${isEnabled ? 'enabled' : 'disabled'} successfully`
    };
  } catch (error) {
    console.error('Error toggling strategy status:', error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to toggle strategy status',
      data: error.response?.data
    };
  }
};

/**
 * B5: Full update strategy (Admin edit functionality)
 * Updates all editable fields of a strategy
 * @param {string} token - Authentication token
 * @param {string|number} strategyId - Strategy ID to update
 * @param {Object} updateData - Full strategy data object
 * @returns {Promise} - Update result
 */
export const fullUpdateStrategy = async (token, strategyId, updateData) => {
  try {
    if (!token) {
      token = localStorage.getItem('token');
    }
    if (!token) {
      throw new Error('No authentication token found');
    }

    console.log('API: Full updating strategy:', strategyId, updateData);

    const response = await axios.put(
      `${baseURL}/api/Strategy/${strategyId}`,
      updateData,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        }
      }
    );

    console.log('API: fullUpdateStrategy response:', response);

    // Handle 204 No Content response (successful update with no response body)
    if (response.status === 204) {
      console.log('Strategy updated successfully (204 No Content)');
      return { success: true, message: 'Strategy updated successfully' };
    }

    // Handle 200 OK response with data
    if (response.status === 200) {
      console.log('Strategy updated successfully (200 OK)');
      return {
        success: true,
        data: response.data,
        message: 'Strategy updated successfully'
      };
    }

    return {
      success: true,
      data: response.data,
      message: 'Strategy updated successfully'
    };
  } catch (error) {
    console.error('Error updating strategy:', error);
    console.error('Error response:', error.response);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to update strategy',
      data: error.response?.data
    };
  }
};

// ============================================
// Q AI ADMIN MANAGEMENT APIs (Section F)
// ============================================

/**
 * F3: Get all Q AI users with token balances
 * @returns {Promise} - List of Q AI users
 */
export const getQAIUsers = async () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    console.log('API: Fetching Q AI users from:', `${baseURL}/api/admin/qai/users`);

    const response = await axios.get(`${baseURL}/api/admin/qai/users`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      }
    });

    console.log('API: getQAIUsers response:', response.data);

    // Handle different response formats
    if (response.data?.success && response.data?.data) {
      return {
        success: true,
        data: response.data.data
      };
    } else if (Array.isArray(response.data)) {
      return {
        success: true,
        data: response.data
      };
    } else {
      return {
        success: true,
        data: response.data?.data || response.data || []
      };
    }
  } catch (error) {
    console.error('Error fetching Q AI users:', error);
    console.error('Error response:', error.response?.data);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to fetch Q AI users',
      data: []
    };
  }
};

/**
 * F5: Credit tokens to a Q AI user
 * @param {string} userId - User ID to credit
 * @param {number} amount - Amount of tokens to credit
 * @param {string} reference - Reference note for the transaction
 * @returns {Promise} - Credit result with new balance
 */
export const creditQAITokens = async (userId, amount, reference) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    console.log('API: Crediting Q AI tokens:', { userId, amount, reference });
    console.log('API: Request URL:', `${baseURL}/api/admin/qai/users/${userId}/credit`);

    const response = await axios.post(
      `${baseURL}/api/admin/qai/users/${userId}/credit`,
      { amount, reference },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        }
      }
    );

    console.log('API: creditQAITokens response:', response.data);

    return {
      success: true,
      data: response.data?.data || response.data,
      message: response.data?.data?.message || `Successfully credited ${amount} tokens`
    };
  } catch (error) {
    console.error('Error crediting Q AI tokens:', error);
    console.error('Error response:', error.response?.data);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to credit tokens'
    };
  }
};

/**
 * Toggle Q AI user active status
 * @param {string} userId - User ID to toggle
 * @param {boolean} isActive - New active status
 * @returns {Promise} - Toggle result
 */
export const toggleQAIUserStatus = async (userId, isActive) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    console.log('API: Toggling Q AI user status:', { userId, isActive });
    console.log('API: Request URL:', `${baseURL}/api/admin/qai/users/${userId}/status`);

    const response = await axios.patch(
      `${baseURL}/api/admin/qai/users/${userId}/status`,
      { isActive },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        }
      }
    );

    console.log('API: toggleQAIUserStatus response:', response.data);

    return {
      success: true,
      data: response.data?.data || response.data,
      message: `Q AI access ${isActive ? 'enabled' : 'disabled'} successfully`
    };
  } catch (error) {
    console.error('Error toggling Q AI user status:', error);
    console.error('Error response:', error.response?.data);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to toggle status'
    };
  }
};

/**
 * Get Q AI user transaction history
 * @param {string} userId - User ID
 * @param {number} limit - Number of transactions to fetch (default 50)
 * @returns {Promise} - Transaction history
 */
export const getQAITransactions = async (userId, limit = 50) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    console.log('API: Fetching Q AI transactions for user:', userId);
    const url = `${baseURL}/api/admin/qai/users/${userId}/transactions?limit=${limit}`;
    console.log('API: Request URL:', url);

    const response = await axios.get(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      }
    });

    console.log('API: getQAITransactions response:', response.data);

    return {
      success: true,
      data: response.data?.data || response.data || []
    };
  } catch (error) {
    console.error('Error fetching Q AI transactions:', error);
    console.error('Error response:', error.response?.data);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to fetch transactions',
      data: []
    };
  }
};

export default adminApi;
